import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import { motion } from 'framer-motion'

import useGetOrganizations from '../../containers/organizations/useGetOrganizations'
import ScreenLoading from '../../components/ScreenLoading'
import mayaLogo from 'images/maya-logo.png'
import usePageTitle from 'containers/pageTitle/usePageTitle'

import './OrganizationSelect.scss'

const OrganizationSelect = () => {
  const { organizations, getOrganizations, loadingOrganizations } = useGetOrganizations()
  const [filteredOrganizations, setFilteredOrganizations] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(null)
  
  const location = useLocation()
  const history = useHistory()
  const [cookies] = useCookies(['user_id'])

  usePageTitle('Select Organization - Maya')

  const getData = async () => {
    await getOrganizations()
  }

  useEffect(() => {
    getData()
  }, [])

  useEffect(() => {
    if (organizations) {
      const filtered = organizations.filter(org =>
        org.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
      setFilteredOrganizations(filtered)
    }
  }, [organizations, searchQuery])

  if (loadingOrganizations) {
    return <ScreenLoading />
  }

  if (!organizations || organizations.length === 0) {
    return (
      <div className="page-column">
        <header>
          <div className="logo">
            <img src={mayaLogo} alt="Maya" />
          </div>
        </header>
        <div className="container-grow">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="container-column organization-select-box"
          >
            <div className="title-container">
              <h1 className="text-align-center">No Organizations Found</h1>
              <div className="secondary-text text-align-center">
                You don't belong to any organizations. Please contact your administrator.
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  const handleOrganizationClick = (organizationId) => {
    if (selectedOrganizationId === organizationId) {
      setSelectedOrganizationId(null)
    } else {
      setSelectedOrganizationId(organizationId)
    }
  }

  const handleAuthorize = () => {
    if (!selectedOrganizationId) return

    const searchParams = new URLSearchParams(location.search)
    const mcpAuthToken = searchParams.get('mcp_auth_token')
    const nextUrl = searchParams.get('next')

    if (mcpAuthToken && nextUrl) {
      // Redirect to dataset selection page with the MCP auth parameters
      const datasetSelectUrl = `/dataset-select?mcp_auth_token=${mcpAuthToken}&next=${encodeURIComponent(nextUrl)}&org_id=${selectedOrganizationId}`
      history.push(datasetSelectUrl)
    }
  }

  return (
    <div className="page-column">
      <header>
        <div className="logo">
          <img src={mayaLogo} alt="Maya" />
        </div>
      </header>
      <div className="container-grow">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="container-column organization-select-box"
        >
          <div className="title-container">
            <h1 className="text-align-center">Select Organization</h1>
            <div className="secondary-text text-align-center">
              Choose the organization you want to authorize access for
            </div>
          </div>

          <div className="search-container">
            <input
              type="text"
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="organizations-list">
            {filteredOrganizations.map(organization => (
              <div
                key={organization.id}
                className={`organization-item ${selectedOrganizationId === organization.id ? 'selected' : ''}`}
                onClick={() => handleOrganizationClick(organization.id)}
              >
                <div className="organization-name">{organization.name}</div>
                <div className="organization-slug">@{organization.slug}</div>
              </div>
            ))}
          </div>

          <div className="authorize-container">
            <button
              className={`submit-button ${!selectedOrganizationId ? 'disabled' : ''}`}
              disabled={!selectedOrganizationId}
              onClick={handleAuthorize}
            >
              Authorize
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default React.memo(OrganizationSelect)